import { useSyncExternalStore } from "react";

// Module store + useSyncExternalStore, mirroring contexts/language.tsx.
// "auto" follows the OS via matchMedia and reacts to live OS switches.

export type ThemeSetting = "light" | "dark" | "auto";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "mandoob:theme";
const LIGHT_THEME_COLOR = "#FFFFFF";
// hsl(160 3% 6%) — the dark page background as hex.
const DARK_THEME_COLOR = "#0F100F";

let currentSetting: ThemeSetting = readStoredTheme() ?? "auto";
let systemPrefersDark = false;
const listeners = new Set<() => void>();

function readStoredTheme(): ThemeSetting | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" || stored === "auto"
      ? stored
      : null;
  } catch {
    return null;
  }
}

export function getTheme(): ThemeSetting {
  return currentSetting;
}

export function getResolvedTheme(): ResolvedTheme {
  if (currentSetting === "auto") return systemPrefersDark ? "dark" : "light";
  return currentSetting;
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function applyResolvedTheme(): void {
  if (typeof document === "undefined") return;
  const dark = getResolvedTheme() === "dark";
  document.documentElement.classList.toggle("dark", dark);
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (meta) {
    meta.setAttribute("content", dark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR);
  }
}

function emit(): void {
  applyResolvedTheme();
  for (const listener of listeners) listener();
}

export function setTheme(setting: ThemeSetting): void {
  currentSetting = setting;
  try {
    if (setting === "auto") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, setting);
  } catch {
    // storage unavailable — theme lives for this page only
  }
  emit();
}

/**
 * Call once before rendering: seeds the system preference, applies the class
 * (the index.html inline script already did a first pass to avoid flashes) and
 * starts listening for live OS theme changes.
 */
export function initTheme(): void {
  if (typeof window === "undefined" || !window.matchMedia) {
    applyResolvedTheme();
    return;
  }
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  systemPrefersDark = query.matches;
  applyResolvedTheme();
  const onChange = (event: MediaQueryListEvent) => {
    systemPrefersDark = event.matches;
    if (currentSetting === "auto") emit();
  };
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", onChange);
  } else {
    // Older WebViews.
    query.addListener(onChange);
  }
}

export function useTheme(): {
  theme: ThemeSetting;
  resolvedTheme: ResolvedTheme;
  setTheme: (setting: ThemeSetting) => void;
} {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, getTheme);
  const resolvedTheme = useSyncExternalStore(
    subscribeTheme,
    getResolvedTheme,
    getResolvedTheme,
  );
  return { theme, resolvedTheme, setTheme };
}
